import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./PropertyBankList.scss";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import PropertyBankTable from "./PropertyBankTable";
import { PropertyApi } from "../../../../utils/api/Property";
import Loading from "../../../loading/Loading";
import ListTopButtonsAndSearch from "./ListTopButtonsAndSearch";
import PropertyBankFilter from "../filter/PropertyBankFilter";
import CloseIcon from "../../../../components/icons/CloseIcon";
import { RootState } from "../../../../redux/store";
import { commonAction } from "../../../../redux/features/commonSlice";

export interface IPropertyBankData {
  _id: string;
  property_ref: string;
  property_address: string;
  property_type_ref?: {
    _id: string;
    name: string;
    slug: string;
  };
  agent_ref?: {
    _id: string;
    name: string;
    email: string;
    image?: string;
    profile_image_ref?: {
      compressed_file_path: string;
    };
  };
  creator_role?: string;
  creator_id?: {
    _id: string;
    fullname?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    profile_image?: string;
    profile_image_ref?: string;
  };
  agency_ref?: {
    _id: string;
    agency_legal_name?: string;
    agency_trading_name?: string;
  };
  property_no?: string;
  property_id?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

let searchTimer: any;

interface stateType {
  isLoading: boolean;
  totalPage: number;
  pageNumber: number;
  totalDocs: number;
  listData: IPropertyBankData[];
  search: string;
}

const PropertyBankList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const resetFilter = useSelector(
    (state: RootState) => state?.common?.resetFilter,
  );

  const [state, setState] = useState<stateType>({
    isLoading: true,
    totalPage: 1,
    pageNumber: 1,
    totalDocs: 0,
    listData: [],
    search: "",
  });

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "00-00-00") return "";
    const parts = dateString.split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateString;
  };

  const formatLabel = (value: string) => {
    if (!value) return "";
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const agency_id = searchParams.get("agency_id") || "";
  const agency_name = searchParams.get("agency_name") || "";
  const agent_id = searchParams.get("agent_id") || "";
  const agent_name = searchParams.get("agent_name") || "";
  const property_type = searchParams.get("property_type") || "";
  const property_type_name = searchParams.get("property_type_name") || "";
  const status = searchParams.get("status") || "";
  const selected_city_ids_str = searchParams.get("selected_city_ids") || "";
  const start_date = searchParams.get("start_date") || "";
  const end_date = searchParams.get("end_date") || "";

  const selected_city_ids = selected_city_ids_str
    ? selected_city_ids_str.split(",").filter(Boolean)
    : [];

  const filterValues = {
    agency_id,
    agency_name,
    agent_id,
    agent_name,
    property_type,
    property_type_name,
    status,
    registration_status: "",
    startDate: start_date || "00-00-00",
    endDate: end_date || "00-00-00",
    selected_city_ids,
  };

  const isFilterActive =
    filterValues.agency_id ||
    filterValues.agent_id ||
    filterValues.property_type ||
    filterValues.status ||
    (filterValues.startDate !== "00-00-00" &&
      filterValues.endDate !== "00-00-00");

  const getListDataApi = async (search: string, page: number) => {
    setState({
      ...state,
      isLoading: true,
    });

    const response = await PropertyApi.getPropertyBankList({
      page: page,
      search: search,
      limit: 30,
      agency_id: agency_id,
      agent_id: agent_id,
      status: status,
      property_type_ids: property_type,
      selected_city_ids: selected_city_ids.toString(),
      selected_state_ids: "",
      selected_country_ids: "",
      start_date: start_date,
      end_date: end_date,
    });

    if (response.data && response.data.success) {
      const {
        docs,
        totalPages,
        page: currentPage,
        totalDocs,
      } = response.data.data;

      setState({
        ...state,
        isLoading: false,
        listData: docs || [],
        totalPage: totalPages,
        pageNumber: currentPage,
        totalDocs: totalDocs,
        search,
      });
    } else {
      setState({
        ...state,
        isLoading: false,
        listData: [],
      });
    }
  };

  const onClickPreview = () => {
    setState({
      ...state,
      pageNumber: state.pageNumber - 1,
    });
  };

  const onClickNext = () => {
    setState({
      ...state,
      pageNumber: state.pageNumber + 1,
    });
  };

  const onChangeSearch = (e: any) => {
    let search = e?.target?.value;
    setState({
      ...state,
      search,
      pageNumber: 1,
    });
  };

  const applyFiltersToUrl = (values: any) => {
    let navigationString = `property-bank?agency_id=${
      values?.agency_id || ""
    }&agency_name=${encodeURIComponent(
      values?.agency_name || "",
    )}&agent_id=${values?.agent_id || ""}&agent_name=${encodeURIComponent(
      values?.agent_name || "",
    )}&property_type=${values?.property_type || ""}&property_type_name=${encodeURIComponent(
      values?.property_type_name || "",
    )}&status=${values?.status || ""}&selected_city_ids=${
      values?.selected_city_ids?.toString() || ""
    }&start_date=${
      values?.startDate === "00-00-00" ? "" : values?.startDate
    }&end_date=${values?.endDate === "00-00-00" ? "" : values?.endDate}`;

    navigate(`/${navigationString}`);
    dispatch(commonAction.resetFilter());
  };

  const handleRemoveAgent = () => {
    applyFiltersToUrl({ ...filterValues, agent_id: "", agent_name: "" });
  };

  const handleRemoveAgency = () => {
    applyFiltersToUrl({ ...filterValues, agency_id: "", agency_name: "" });
  };

  const handleRemovePropertyType = () => {
    applyFiltersToUrl({
      ...filterValues,
      property_type: "",
      property_type_name: "",
    });
  };

  const handleRemoveStatus = () => {
    applyFiltersToUrl({ ...filterValues, status: "" });
  };

  const handleRemoveDate = () => {
    applyFiltersToUrl({
      ...filterValues,
      startDate: "00-00-00",
      endDate: "00-00-00",
    });
  };

  const handleClearFilters = () => {
    navigate("/property-bank");
    dispatch(commonAction.resetFilter());
  };

  useEffect(() => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      getListDataApi(state?.search, state?.pageNumber);
    }, 500);
  }, [state?.search, state?.pageNumber]);

  useEffect(() => {
    getListDataApi(state?.search, state?.pageNumber);
  }, [resetFilter, searchParams]);

  const startRange = (state.pageNumber - 1) * 30 + 1;
  const endRange = Math.min(state.pageNumber * 30, state.totalDocs);

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="property-bank-list-container">
          <div className="list-heading">
            <p>Property bank</p>
          </div>

          <ListTopButtonsAndSearch
            add_btn_name="Add"
            add_btn_link="/property-bank/create"
            reset_btn_link="/property-bank"
            placeholder="Search by address or name"
            onChangeSearch={onChangeSearch}
            type="property_bank"
          />

          {isFilterActive && (
            <div className="applied-filters-container">
              <p className="applied-label font-inter-400">Applied filters</p>
              <div className="chips-wrapper">
                {filterValues.agent_id && (
                  <div className="filter-chip">
                    <span className="value font-inter-400">
                      Agent: {filterValues.agent_name || filterValues.agent_id}
                    </span>
                    <div className="close-btn" onClick={handleRemoveAgent}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.agency_id && (
                  <div className="filter-chip">
                    <span className="value font-inter-400">
                      Agency:{" "}
                      {filterValues.agency_name || filterValues.agency_id}
                    </span>
                    <div className="close-btn" onClick={handleRemoveAgency}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.property_type && (
                  <div className="filter-chip">
                    <span className="value font-inter-400">
                      Property type:{" "}
                      {filterValues.property_type_name ||
                        filterValues.property_type}
                    </span>
                    <div
                      className="close-btn"
                      onClick={handleRemovePropertyType}
                    >
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.status && (
                  <div className="filter-chip">
                    <span className="value font-inter-400">
                      Status: {formatLabel(filterValues.status)}
                    </span>
                    <div className="close-btn" onClick={handleRemoveStatus}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.startDate !== "00-00-00" && (
                  <div className="filter-chip">
                    <span className="value font-inter-400">
                      Date created: {formatDate(filterValues.startDate)} -{" "}
                      {formatDate(filterValues.endDate)}
                    </span>
                    <div className="close-btn" onClick={handleRemoveDate}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                <div
                  className="clear-all-btn font-inter-400"
                  onClick={handleClearFilters}
                >
                  Clear all filters
                </div>
              </div>
            </div>
          )}

          {state.isLoading ? (
            <div className="data-not-found">
              <Loading />
            </div>
          ) : state.listData.length === 0 ? (
            <div className="data-not-found">
              <p>List is Empty!</p>
            </div>
          ) : (
            <PropertyBankTable listData={state.listData} />
          )}

          {!state.isLoading && state.listData.length > 0 && (
            <div className="pagination-wrapper">
              <div className="viewing-text">
                Viewing {state.totalDocs > 0 ? startRange : 0}-{endRange} of{" "}
                {state.totalDocs} results
              </div>
              <div className="custom-pagination">
                <button
                  className="page-btn"
                  disabled={state.pageNumber === 1 || state.isLoading}
                  onClick={onClickPreview}
                >
                  ‹
                </button>

                <button
                  className="page-btn"
                  disabled={
                    state.pageNumber === state.totalPage || state.isLoading
                  }
                  onClick={onClickNext}
                >
                  ›
                </button>
              </div>
            </div>
          )}

          <PropertyBankFilter />
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default PropertyBankList;
